import moment from "moment";

/** Collection mechanism id: percentage-based. */
export const MECHANISM_PERCENT = 2;
/** Collection mechanism id: full (100%) collection. */
export const MECHANISM_FULL = 1;

/** Default remote table sort for the debt list. */
export const defaultSorted = [{ dataField: "unique_reference", order: "asc" }];

/** Page size options for the debt collection table paginator. */
export const sizePerPageList = [
  { text: "20", value: 20 },
  { text: "50", value: 50 },
  { text: "100", value: 100 },
];

/** Initial filter/sort/pagination state for the debt collection list. */
export const initialFilter = {
  filter: {
    search: "",
    franchise_id: "",
    status: "",
    started_at: "",
  },
  sortOrder: "asc",
  sortField: "unique_reference",
  pageNumber: 1,
  pageSize: 20,
};

/**
 * Maps bootstrap-table column field names to API `sort_by` keys.
 * @param {string|null|undefined} dataField - Column `dataField` from the table.
 * @returns {string} API sort field name.
 */
export function mapDebtTableFieldToSortBy(dataField) {
  if (!dataField) return "id";
  const exceptions = {
    created_by: "created_at",
    franchise: "franchise_id",
  };
  if (exceptions[dataField]) return exceptions[dataField];
  return dataField;
}

/**
 * Builds query object for the franchise debt list API from UI state.
 * @param {object|null|undefined} [queryParams={}] - UI query state (filter, sort, page); nullish is treated as empty.
 * @param {{ scopedFranchiseId?: string|number|null }} [options={}] - When set, forces franchise scope for franchise owners.
 * @returns {Record<string, string|number>} Flat query parameters for `listFranchiseDebt`.
 */
export function buildDebtListApiQuery(queryParams = {}, options = {}) {
  const qp = queryParams ?? {};
  const q = {};
  const page = Math.max(1, Number(qp.pageNumber) || 1);
  const perPage = Math.max(1, Number(qp.pageSize) || 20);
  q.page = page;
  q.per_page = perPage;

  const f = qp.filter || {};
  const scopedRaw = options.scopedFranchiseId;
  const scoped =
    scopedRaw != null && String(scopedRaw).trim() !== "";
  if (scoped) {
    q.franchise_id = String(scopedRaw).trim();
  } else if (f.franchise_id != null && String(f.franchise_id).trim() !== "") {
    q.franchise_id = String(f.franchise_id).trim();
  }
  if (
    f.status !== "" &&
    f.status != null &&
    f.status !== undefined &&
    String(f.status).trim() !== ""
  ) {
    const n = Number(f.status);
    if (Number.isFinite(n)) q.status = n;
  }
  if (f.started_at) {
    const m = moment(f.started_at);
    if (m.isValid()) q.started_at = m.format("YYYY-MM-DD");
  }

  const search = f.search != null ? String(f.search).trim() : "";
  if (search) {
    q.search_keyword = search;
  }

  const sortField =
    qp.sortField || defaultSorted[0].dataField;
  const sortOrder = (
    qp.sortOrder ||
    defaultSorted[0].order ||
    "asc"
  ).toLowerCase();
  q.sort_by = mapDebtTableFieldToSortBy(sortField);
  q.sort_dir = sortOrder === "desc" ? "desc" : "asc";

  return q;
}

/**
 * Builds export API query params from list UI state (no pagination; optional franchise omission).
 * @param {object} queryParams - UI query state (filter, sort).
 * @param {{ scopedFranchiseId?: string|number|null, omitFranchiseIdForExport?: boolean }} [options={}] - Same scoped franchise as list; set `omitFranchiseIdForExport` to drop franchise id from the export query.
 * @returns {Record<string, string|number>} Query object for CSV export endpoints.
 */
export function buildDebtListExportApiQuery(queryParams, options = {}) {
  const { omitFranchiseIdForExport = false, ...listOptions } = options;
  const full = buildDebtListApiQuery(queryParams, listOptions);
  const rest = { ...full };
  delete rest.page;
  delete rest.per_page;
  if (omitFranchiseIdForExport) {
    delete rest.franchise_id;
  }
  return rest;
}

/**
 * Normalizes various list API response shapes into rows and total count.
 * @param {import("axios").AxiosResponse|{ data?: unknown }} res - Axios response or similar.
 * @returns {{ rows: object[], total: number }} Table rows and total for pagination.
 */
export function extractDebtListRowsAndTotal(res) {
  const body = res?.data;
  let rows = [];
  let total = 0;

  if (Array.isArray(body?.data)) {
    rows = body.data;
    total = Number(body.total);
  } else if (Array.isArray(body)) {
    rows = body;
    total = rows.length;
  } else if (body?.data != null && Array.isArray(body.data.data)) {
    rows = body.data.data;
    total = Number(body.data.total ?? body.total);
  } else if (typeof body?.data === "object" && body.data?.id != null) {
    rows = [body.data];
  }

  if (!Number.isFinite(total) || total < 0) {
    total = rows.length;
  }

  rows = Array.isArray(rows)
    ? rows.filter((r) => r != null && typeof r === "object")
    : [];

  return { rows, total };
}

/** Status filter dropdown options for the debt collection list (values match API codes). */
export const DEBT_LIST_STATUS_FILTER_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "0", label: "Pending" },
  { value: "1", label: "Ongoing" },
  { value: "2", label: "Paused" },
  { value: "3", label: "Completed" },
  { value: "4", label: "Closed" },
];

/**
 * Formats a numeric amount as GBP for list cells.
 * @param {string|number|null|undefined} value - Raw monetary value.
 * @returns {string} Formatted currency or em dash when missing or invalid.
 */
export function formatDebtMoney(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  }).format(n);
}

/**
 * Builds a display string for a user reference object or id field on a row.
 * @param {string|object|null|undefined} u - User payload or string id.
 * @returns {string} Display name or empty string (callers may fallback).
 */
export function formatDebtUserRef(u) {
  if (u == null || u === "") return "";
  if (typeof u === "string") return u;
  if (typeof u === "object") {
    const fn = u.first_name || u.firstName;
    const ln = u.last_name || u.lastName;
    const joined = [fn, ln].filter(Boolean).join(" ");
    if (joined) return joined;
    return u.name || u.email || "";
  }
  return String(u);
}

/**
 * Human-readable label for a collection mechanism code.
 * @param {string|number|null|undefined} mechanism - Mechanism id from the API.
 * @returns {string} Label for tables and UI.
 */
export function collectionMechanismLabel(mechanism) {
  const n = Number(mechanism);
  if (n === MECHANISM_FULL) return "100% Collection";
  if (n === MECHANISM_PERCENT) return "% Based Collection";
  return mechanism != null && mechanism !== "" ? String(mechanism) : "—";
}

/**
 * Formats the collection percent column for a debt list row.
 * @param {object} row - Debt list row from the API.
 * @returns {string} Percentage text, 100% for full mechanism, or em dash.
 */
export function formatCollectionPercentCell(row) {
  const m = Number(row.collection_mechanism);
  if (m === MECHANISM_FULL) return "100%";
  const p = row.collection_percent;
  if (p == null || p === "") return "—";
  const n = Number(String(p).replace(/,/g, ""));
  if (Number.isNaN(n)) return String(p);
  return `${n}%`;
}

/**
 * Resolves franchise display name from a nested franchise object on a row.
 * @param {object} row - Debt list row from the API.
 * @returns {string} Franchise name or em dash.
 */
export function formatFranchiseNameCell(row) {
  const f = row.franchise;
  if (f && typeof f === "object") {
    return f.name || f.short_name || "—";
  }
  return "—";
}
